import React, { useEffect, useRef } from 'react';

function drawLine(container, slotEl, tooltipEl) {
  const containerRect = container.getBoundingClientRect();
  const slotRect = slotEl.getBoundingClientRect();
  const tooltipRect = tooltipEl.getBoundingClientRect();
  const x1 = slotRect.left + slotRect.width / 2 - containerRect.left;
  const y1 = slotRect.top + slotRect.height / 2 - containerRect.top;
  // Anchor to right side of tooltip if it's to the left of the slot
  const tooltipIsLeft = tooltipRect.right < slotRect.left;
  const x2 = tooltipIsLeft ? tooltipRect.right - containerRect.left : tooltipRect.left - containerRect.left + 4;
  const y2 = tooltipRect.top + tooltipRect.height / 2 - containerRect.top;
  const midX = x1 + (x2 - x1) * 0.3;
  const midY = y2 + 10;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:15;overflow:visible;';
  svg.innerHTML = `<polyline points="${x1},${y1} ${midX},${midY} ${x2},${y2}" fill="none" stroke="#ff2222" stroke-width="1.5" stroke-dasharray="4,3" opacity="0.7"><animate attributeName="stroke-dashoffset" from="0" to="-14" dur="1s" repeatCount="indefinite"/></polyline><circle cx="${x1}" cy="${y1}" r="3" fill="#ff2222" opacity="0.8"/><circle cx="${x2}" cy="${y2}" r="2" fill="#ff2222" opacity="0.6"/>`;
  return svg;
}

export default function LandingPage() {
  const showcaseRef = useRef(null);

  useEffect(() => {
    const container = showcaseRef.current;
    if (!container) return;

    const slots = ['head', 'chest', 'hands', 'feet'];
    const glowColors = ['#ff8000', '#a335ee', '#1eff00', '#ff8000'];
    let currentIndex = 0;
    let activeSvg = null;
    let activeSlot = null;

    function cycle() {
      if (activeSvg) { activeSvg.remove(); activeSvg = null; }
      if (activeSlot) { activeSlot.style.borderColor = ''; activeSlot.style.boxShadow = ''; }
      container.querySelectorAll('.hero-tooltip').forEach(t => t.style.opacity = '0');

      const slot = container.querySelector(`.hero-slot-${slots[currentIndex]}`);
      const tooltip = container.querySelector(`.tooltip-${currentIndex + 1}`);
      if (slot && tooltip) {
        tooltip.style.opacity = '1';
        slot.style.borderColor = glowColors[currentIndex];
        slot.style.boxShadow = `0 0 12px ${glowColors[currentIndex]}80`;
        activeSlot = slot;
        activeSvg = drawLine(container, slot, tooltip);
        container.appendChild(activeSvg);
      }
      currentIndex = (currentIndex + 1) % slots.length;
    }

    // Start cycling
    cycle();
    const interval = setInterval(cycle, 3000);
    return () => { clearInterval(interval); if (activeSvg) activeSvg.remove(); if (activeSlot) { activeSlot.style.borderColor = ''; activeSlot.style.boxShadow = ''; } };
  }, []);

  return (
    <div className="landing">
      <div style={{ position:'fixed',inset:0,background:'url(panopticon.gif) center/cover no-repeat',opacity:0.025,pointerEvents:'none',zIndex:0 }}/>

      <nav className="landing-nav">
        <div className="landing-logo">🔮 DNDLorewalkerService</div>
        <a href="https://6uqbiwvsfh.execute-api.us-east-1.amazonaws.com/prod/auth/login" className="landing-cta-sm">Launch Console →</a>
      </nav>

      <section className="landing-hero">
        <div className="hero-content">
          <h1>Run your D&D campaign<br/>from a <span className="accent shine">command center.</span></h1>
          <p className="hero-sub">Automated combat. Live dashboards. Total oversight.<br/>Your players explore. You observe.<br/><span style={{fontSize:'0.7rem',color:'var(--text-dim)'}}>A Discord-integrated D&D 5e companion — your bot runs the game, your dashboard runs the war room.</span></p>
          <div className="hero-actions">
            <a href="https://6uqbiwvsfh.execute-api.us-east-1.amazonaws.com/prod/auth/login" className="btn-primary">Get Started — Free</a>
            <a href="/login" className="btn-ghost" style={{marginLeft:'12px'}}>Email Sign Up →</a>
            <a href="#features" className="btn-ghost">See Features ↓</a>
          </div>
        </div>
        <div className="hero-visual">
          <div className="hero-body-showcase" ref={showcaseRef}>
            <img src="body_model_transparent.png" className="hero-body-img" alt="character" />
            <div className="hero-slot hero-slot-head"></div>
            <div className="hero-slot hero-slot-chest"></div>
            <div className="hero-slot hero-slot-hands"></div>
            <div className="hero-slot hero-slot-feet"></div>
            <div className="hero-slot hero-slot-ring"></div>
            <div className="hero-tooltip tooltip-1" style={{opacity:0,transition:'opacity 0.3s'}}>
              <div className="ht-name legendary">Crown of the Eternal</div>
              <div className="ht-stats">Legendary | +5 Spell DC</div>
            </div>
            <div className="hero-tooltip tooltip-2" style={{opacity:0,transition:'opacity 0.3s'}}>
              <div className="ht-name rare">Voidweave Robes</div>
              <div className="ht-stats">AC 11 | Resist: psychic</div>
            </div>
            <div className="hero-tooltip tooltip-3" style={{opacity:0,transition:'opacity 0.3s'}}>
              <div className="ht-name uncommon">Tidecaller Axe</div>
              <div className="ht-stats">1d6+1 | +1d4 cold</div>
            </div>
            <div className="hero-tooltip tooltip-4" style={{opacity:0,transition:'opacity 0.3s'}}>
              <div className="ht-name legendary">Voidstrider Boots</div>
              <div className="ht-stats">Legendary | +60 ft Speed</div>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-features" id="features">
        <h2>Everything a DM needs. Nothing they don't.</h2>
        <div className="features-grid">
          <div className="feature-card"><span className="feature-icon">⚔️</span><h3>Automated Combat</h3><p>Initiative tracking, turn timers, enemy AI attacks, auto-advance. You run the story — the bot runs the math.</p></div>
          <div className="feature-card"><span className="feature-icon">🔮</span><h3>Live Dashboard</h3><p>Real-time HP, equipment, conditions. See every player's state update as combat unfolds.</p></div>
          <div className="feature-card"><span className="feature-icon">⚡</span><h3>Web Command Center</h3><p>Send DM commands from your browser. Heal, spawn, buff — click a character, execute instantly in Discord.</p></div>
          <div className="feature-card"><span className="feature-icon">🎲</span><h3>469 Spells. 35 Enemies.</h3><p>Faithful to 5e. Per-class spell lists, slot management, Need/Greed loot, weapon effects, racial abilities.</p></div>
          <div className="feature-card"><span className="feature-icon">💀</span><h3>Buff & Debuff System</h3><p>Mechanical effects that matter. Disadvantage, DOT, stun skips, resistance. Turn-based and time-based expiry.</p></div>
          <div className="feature-card"><span className="feature-icon">🎯</span><h3>Click-to-Target</h3><p>Click a button. Click a character. Command executes. No typing @mentions. No copy-pasting IDs.</p></div>
        </div>
      </section>

      {/* Showcase Tiles */}
      <section className="landing-showcase" id="showcase">
        <h2>See It In Action</h2>
        <div className="showcase-grid">
          {/* Radar Tile — exact replica */}
          <div className="showcase-tile">
            <div className="showcase-radar">
              <svg width="100%" height="100%" viewBox="0 0 200 200">
                <circle cx="100" cy="100" r="30" fill="none" stroke="rgba(157,78,221,0.15)" strokeWidth="0.5" />
                <circle cx="100" cy="100" r="60" fill="none" stroke="rgba(157,78,221,0.15)" strokeWidth="0.5" />
                <circle cx="100" cy="100" r="90" fill="none" stroke="rgba(157,78,221,0.15)" strokeWidth="0.5" />
                <line x1="100" y1="0" x2="100" y2="200" stroke="rgba(157,78,221,0.08)" strokeWidth="0.5" />
                <line x1="0" y1="100" x2="200" y2="100" stroke="rgba(157,78,221,0.08)" strokeWidth="0.5" />
                <text x="104" y="75" className="radar-zone-text" style={{fontSize:'7px',fill:'rgba(157,78,221,0.4)'}}>MELEE</text>
                <text x="104" y="45" className="radar-zone-text" style={{fontSize:'7px',fill:'rgba(157,78,221,0.4)'}}>CLOSE</text>
                <text x="104" y="15" className="radar-zone-text" style={{fontSize:'7px',fill:'rgba(157,78,221,0.4)'}}>FAR</text>
              </svg>
              <div className="showcase-sweep-overlay"></div>
              <div className="showcase-token player" style={{top:'42%',left:'46%'}}><span className="token-label">Sigurtyr</span></div>
              <div className="showcase-token enemy" style={{top:'30%',left:'65%'}}><span className="token-label">Slappy A</span></div>
              <div className="showcase-token enemy" style={{top:'62%',left:'35%'}}><span className="token-label">Slappy B</span></div>
            </div>
            <h4>Tactical Radar</h4>
            <p>Drag tokens between zones. See enemy positions update in real-time.</p>
          </div>

          {/* Player Card Tile — exact replica */}
          <div className="showcase-tile">
            <div className="player-card">
              <div className="body-diagram">
                <div className="body-img-container">
                  <img src="body_model_transparent.png" className="body-img" alt="body" />
                  <div className="slot-overlay head equipped" data-rarity="Legendary"></div>
                  <div className="slot-overlay chest equipped" data-rarity="Artifact"></div>
                  <div className="slot-overlay hands equipped" data-rarity="Very Rare"></div>
                  <div className="slot-overlay feet equipped" data-rarity="Rare"></div>
                </div>
              </div>
              <div className="player-info">
                <div className="name">Vaelith Dawnbringer <span className="cond-icon buff">🛡️</span><span className="cond-icon buff">🔥</span></div>
                <div className="active-beacon"></div>
                <div className="class-race">Lv 14 Human Paladin (Oath of Devotion)</div>
                <div className="bars">
                  <div className="bar-row"><span className="label">HP</span><div className="bar"><div className="fill hp" style={{width:'85%'}}></div></div><span className="value">89 / 105</span></div>
                  <div className="bar-row"><span className="label">AC</span><div className="bar"><div className="fill mp" style={{width:'100%'}}></div></div><span className="value">21</span></div>
                </div>
                <div className="mod-row">
                  <div className="mod-badge"><span className="mod-label">STR</span><span className="mod-val positive">+4</span></div>
                  <div className="mod-badge"><span className="mod-label">DEX</span><span className="mod-val">+1</span></div>
                  <div className="mod-badge"><span className="mod-label">CON</span><span className="mod-val positive">+3</span></div>
                  <div className="mod-badge"><span className="mod-label">INT</span><span className="mod-val">+0</span></div>
                  <div className="mod-badge"><span className="mod-label">WIS</span><span className="mod-val positive">+2</span></div>
                  <div className="mod-badge"><span className="mod-label">CHA</span><span className="mod-val positive">+4</span></div>
                </div>
              </div>
            </div>
            <h4>Live Player Cards</h4>
            <p>HP, buffs, gear, conditions — all updating in real-time as combat unfolds.</p>
          </div>

          {/* Guild Page Tile — exact rip intro */}
          <div className="showcase-tile">
            <div className="showcase-guild">
              <div className="sg-rip-top"></div>
              <div className="sg-rip-bottom"></div>
              <div className="showcase-guild-parallax"></div>
              <div className="showcase-guild-embers">
                {Array.from({length:6}).map((_,i) => <div key={i} className="ember-particle" style={{left:`${15+Math.random()*70}%`,bottom:`${Math.random()*30}%`,animationDelay:`${Math.random()*6}s`,animationDuration:`${4+Math.random()*4}s`}} />)}
              </div>
              <div className="sg-content">
                <span className="sg-name">⚔️ The Drowned King's Court</span>
                <span className="sg-tag">"Where the tide rises, heroes fall."</span>
                <div className="sg-stats">
                  <span>47 Combats</span>
                  <span>312 Slain</span>
                  <span>89 Spells</span>
                </div>
              </div>
            </div>
            <h4>Public Guild Page</h4>
            <p>Your own page with parallax, achievements, roster, and recruitment.</p>
          </div>
        </div>
      </section>

      <section className="landing-pricing" id="pricing">
        <h2>Simple pricing. Start free.</h2>
        <div className="pricing-grid">
          <div className="price-card">
            <h3>Adventurer</h3>
            <p style={{fontSize:'0.65rem',color:'var(--text-dim)',margin:'-4px 0 8px'}}>Free</p>
            <div className="price">$0<span>/forever</span></div>
            <ul><li>✅ Full Discord bot</li><li>✅ Automated combat engine</li><li>✅ Up to 4 players</li><li>✅ Live dashboard (view only)</li><li>❌ Web command center</li><li>❌ Combat VFX</li><li>❌ Tactical radar</li><li>❌ AI Assistant</li></ul>
            <a href="https://6uqbiwvsfh.execute-api.us-east-1.amazonaws.com/prod/auth/login" className="btn-primary">Get Started</a>
          </div>
          <div className="price-card featured">
            <div className="price-badge">MOST POPULAR</div>
            <h3>Steward</h3>
            <p style={{fontSize:'0.65rem',color:'var(--text-dim)',margin:'-4px 0 8px'}}>Pro</p>
            <div className="price">$8<span>/month</span></div>
            <ul><li>✅ Everything in Free</li><li>✅ Unlimited players</li><li>✅ Web command center</li><li>✅ Real-time combat VFX</li><li>✅ Spellbook browser (469 spells)</li><li>✅ Omen narrative engine</li><li>❌ Tactical radar & zones</li><li>❌ AI Campaign Assistant</li><li>❌ Custom enemy creator</li></ul>
            <a href="https://6uqbiwvsfh.execute-api.us-east-1.amazonaws.com/prod/auth/login?upgrade=true" className="btn-primary btn-subscribe">Subscribe — $8/mo</a>
          </div>
          <div className="price-card">
            <div className="price-badge" style={{background:'linear-gradient(135deg,#9d4edd,#4a1942)'}}>AI-POWERED</div>
            <h3>Lorewalker</h3>
            <p style={{fontSize:'0.65rem',color:'var(--text-dim)',margin:'-4px 0 8px'}}>Pro+</p>
            <div className="price">$15<span>/month</span></div>
            <ul><li>✅ Everything in Pro</li><li>✅ Tactical radar & zone system</li><li>✅ AI Campaign Assistant</li><li>✅ AI session prep & summaries</li><li>✅ AI encounter balancing</li><li>✅ AI NPC & loot generation</li><li>✅ Custom enemy creator</li><li>✅ Campaign guide drawer</li><li>✅ 30 AI calls/month</li><li>✅ Early access to new features</li></ul>
            <a href="https://6uqbiwvsfh.execute-api.us-east-1.amazonaws.com/prod/auth/login?upgrade=true&plan=pro_plus" className="btn-primary btn-subscribe">Subscribe — $15/mo</a>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <p>🔮 DNDLorewalkerService — Built for Dungeon Masters who demand total control.</p>
        <p className="footer-sub">Lorewalker's Panopticon™</p>
        <p style={{marginTop:'12px'}}><a href="/terms.md" style={{color:'#8a7e6e',fontSize:'0.7rem',textDecoration:'underline'}}>Terms of Service</a> · <a href="/terms.md#10-data--privacy" style={{color:'#8a7e6e',fontSize:'0.7rem',textDecoration:'underline'}}>Privacy Policy</a></p>
      </footer>
    </div>
  );
}
